import React from 'react';
import { Card } from '@/components/ui/cards/Card';
import { Text } from '@/components/ui/text/Text';
import { colors } from '@/assets/styles/colors';


export interface SelectionModalOption {
  title: string;
  description: string;
  onClick?: () => void;
  disabled?: boolean;
}

export interface SelectionModalProps {
  title: string;
  subtitle: string;
  options: SelectionModalOption[];
  testIdPrefix?: string;
  'data-testid'?: string;
}

const SelectionModalOptionItem: React.FC<SelectionModalOption & { testIdPrefix: string }> = ({
  title,
  description,
  onClick,
  disabled = false,
  testIdPrefix,
}) => {
  const isClickable = !disabled && !!onClick;

  return (
    <div
      className={`flex flex-col gap-2 py-5 px-2 ${isClickable ? 'cursor-pointer hover:bg-blue-50 transition-colors rounded-lg' : 'opacity-50 cursor-not-allowed'}`}
      onClick={isClickable ? onClick : undefined}
      role={isClickable ? 'button' : undefined}
      tabIndex={isClickable ? 0 : undefined}
      onKeyDown={isClickable ? (event) => { if (event.key === 'Enter' || event.key === ' ') { event.preventDefault(); onClick?.(); } } : undefined}
      data-testid={`${testIdPrefix}-option-${title.toLowerCase().replace(/\s+/g, '-')}`}
    >
      <div className="flex items-center gap-3">
        <span
          className="flex-shrink-0 text-2xl font-bold leading-none"
          style={{ color: colors.important.main }}
          aria-hidden="true"
        >
          +
        </span>
        <Text
          variant="body-title-medium"
          style={{ color: colors.primary.main }}
        >
          {title}
        </Text>
      </div>
      <Text
        as="p"
        variant="small-regular"
        color={colors.grays.neutral66}
        lineHeight="150%"
      >
        {description}
      </Text>
    </div>
  );
};

export const SelectionModal: React.FC<SelectionModalProps> = ({
  title,
  subtitle,
  options,
  testIdPrefix = 'selection-modal',
  'data-testid': dataTestId = 'selection-modal',
}) => (
  <div onClick={(event) => event.stopPropagation()} data-testid={dataTestId}>
    <Card
      elevation={2}
      padding="24px"
      borderRadius="16px"
      className="flex flex-col w-full max-w-[360px]"
    >
      <div className="flex flex-col gap-1 pb-2">
        <Text variant="body-title-medium" style={{ color: colors.primary.main }}>
          {title}
        </Text>
        <Text as="p" variant="small-regular" color={colors.grays.neutral66} lineHeight="150%">
          {subtitle}
        </Text>
      </div>

      <div className="flex flex-col">
        {options.map((option, index) => (
          <React.Fragment key={option.title}>
            {index > 0 && <div className="border-t border-gray-200" />}
            <SelectionModalOptionItem {...option} testIdPrefix={testIdPrefix} />
          </React.Fragment>
        ))}
      </div>
    </Card>
  </div>
);

export default SelectionModal;
